<template>
  <a-modal
    v-model="visible"
    title="点云数据查看器"
    width="90%"
    :footer="null"
    @cancel="handleClose"
    :destroyOnClose="true"
  >
    <div class="pcd-viewer-container">
      <div ref="canvasContainer" class="canvas-container"></div>
      <div class="loading-overlay" v-if="loading">
        <a-spin size="large" :tip="loadingText" />
      </div>
      <div class="controls">
        <a-button icon="zoom-in" @click="resetView">重置视图</a-button>
        <a-button icon="download" @click="handleDownloadPCD">下载 PCD 文件</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import * as THREE from 'three'
import pako from 'pako'

export default {
  name: 'PcdViewer',
  data() {
    return {
      loading: false,
      loadingText: '加载中...',
      currentFileUrl: '',
      currentFileName: '',
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      pointCloud: null,
      animationId: null,
      localVisible: false
    }
  },
  computed: {
    visible: {
      get() {
        return this.localVisible
      },
      set(val) {
        this.localVisible = val
      }
    }
  },
  watch: {
    localVisible(val) {
      if (val) {
        this.$nextTick(() => {
          // 每次都重新初始化 Three.js
          this.initThree()
          // 然后在有 URL 时才加载
          if (this.currentFileUrl) {
            this.loadPCDFile(this.currentFileUrl)
          }
        })
      } else {
        this.clearPointCloud()
      }
    }
  },
  mounted() {
    // 不在 mounted 时初始化，等 visible 变为 true 时再初始化
  },
  beforeDestroy() {
    this.dispose()
  },
  methods: {
    show(fileUrl, fileName) {
      console.log('打开点云查看器:', fileUrl, fileName)
      this.currentFileUrl = fileUrl
      this.currentFileName = fileName
      
      // 先清理旧的点云
      this.clearPointCloud()
      
      // 然后打开弹窗
      this.visible = true
      
      // 弹窗打开后会自动触发 watch 中的 loadPCDFile
    },
    
    handleClose() {
      console.log('关闭点云查看器')
      this.visible = false
      // 延迟清理，确保弹窗动画完成
      setTimeout(() => {
        this.clearPointCloud()
      }, 300)
    },
    
    initThree() {
      const container = this.$refs.canvasContainer
      if (!container) {
        console.error('canvasContainer 引用不存在')
        return
      }
      
      console.log('初始化 Three.js，当前 scene:', !!this.scene, 'renderer:', !!this.renderer)
      
      // 如果已经有场景和渲染器，先完全清理
      if (this.scene || this.renderer) {
        console.log('检测到已有 Three.js 实例，先清理')
        this.dispose()
      }
      
      // 创建全新的场景
      this.scene = new THREE.Scene()
      this.scene.background = new THREE.Color(0x1a1a2e)
      console.log('创建新场景')
      
      // 创建相机
      this.camera = new THREE.PerspectiveCamera(
        75,
        container.clientWidth / container.clientHeight,
        0.1,
        10000
      )
      this.camera.position.set(0, 0, 100)
      console.log('创建相机，位置:', this.camera.position)
      
      // 创建渲染器
      this.renderer = new THREE.WebGLRenderer({ antialias: true })
      this.renderer.setSize(container.clientWidth, container.clientHeight)
      this.renderer.setPixelRatio(window.devicePixelRatio)
      container.appendChild(this.renderer.domElement)
      console.log('创建渲染器，尺寸:', container.clientWidth, 'x', container.clientHeight)
      
      // 添加坐标轴辅助
      const axesHelper = new THREE.AxesHelper(50)
      this.scene.add(axesHelper)
      console.log('添加坐标轴，场景子对象数:', this.scene.children.length)
      
      // 添加网格辅助
      const gridHelper = new THREE.GridHelper(100, 10)
      this.scene.add(gridHelper)
      console.log('添加网格，场景子对象数:', this.scene.children.length)
      
      // 简单的轨道控制（手动实现）
      this.setupControls()
      console.log('设置控制器')
      
      // 开始渲染循环
      this.animate()
      console.log('开始渲染循环')
      
      // 监听窗口大小变化
      window.addEventListener('resize', this.onWindowResize)
      
      console.log('=== Three.js 初始化完成 ===')
      console.log('场景:', !!this.scene)
      console.log('相机:', !!this.camera)
      console.log('渲染器:', !!this.renderer)
      console.log('控制器:', !!this.controls)
      console.log('场景子对象:', this.scene.children.length)
    },
    
    setupControls() {
      let isDragging = false
      let previousMousePosition = { x: 0, y: 0 }
      let spherical = { radius: 100, theta: Math.PI / 4, phi: Math.PI / 4 }
      
      const canvas = this.renderer.domElement
      
      canvas.addEventListener('mousedown', (e) => {
        isDragging = true
        previousMousePosition = { x: e.clientX, y: e.clientY }
      })
      
      canvas.addEventListener('mousemove', (e) => {
        if (!isDragging) return
        
        const deltaX = e.clientX - previousMousePosition.x
        const deltaY = e.clientY - previousMousePosition.y
        
        spherical.theta -= deltaX * 0.01
        spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi + deltaY * 0.01))
        
        this.updateCameraPosition(spherical)
        previousMousePosition = { x: e.clientX, y: e.clientY }
      })
      
      canvas.addEventListener('mouseup', () => {
        isDragging = false
      })
      
      canvas.addEventListener('wheel', (e) => {
        e.preventDefault()
        spherical.radius += e.deltaY * 0.1
        spherical.radius = Math.max(10, Math.min(1000, spherical.radius))
        this.updateCameraPosition(spherical)
      }, { passive: false })
      
      this.controls = { spherical }
    },
    
    updateCameraPosition(spherical) {
      const { radius, theta, phi } = spherical
      this.camera.position.x = radius * Math.sin(phi) * Math.sin(theta)
      this.camera.position.y = radius * Math.cos(phi)
      this.camera.position.z = radius * Math.sin(phi) * Math.cos(theta)
      this.camera.lookAt(0, 0, 0)
    },
    
    animate() {
      this.animationId = requestAnimationFrame(() => this.animate())
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera)
      }
    },
    
    onWindowResize() {
      const container = this.$refs.canvasContainer
      if (!container || !this.camera || !this.renderer) return
      
      this.camera.aspect = container.clientWidth / container.clientHeight
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(container.clientWidth, container.clientHeight)
    },
    
    async loadPCDFile(url) {
      this.loading = true
      this.loadingText = '正在下载文件...'
      
      return new Promise((resolve, reject) => {
        console.log('开始下载 PCD 文件:', url)
        
        // 尝试直接使用 XHR 下载
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url, true)
        xhr.responseType = 'blob'
        xhr.setRequestHeader('Accept', '*/*')
        
        // 添加更多调试信息
        xhr.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100)
            console.log(`下载进度：${percent}%`)
            this.loadingText = `正在下载文件... ${percent}%`
          }
        }
        
        xhr.onload = () => {
          console.log('XHR 响应状态:', xhr.status)
          
          if (xhr.status === 200) {
            const blob = xhr.response
            console.log('下载成功，Blob 大小:', blob.size)
            this.processBlob(blob, resolve, reject)
          } else {
            console.error('直接下载失败，状态码:', xhr.status)
            this.loading = false
            this.$message.error(`下载失败：${xhr.status}`)
            reject(new Error(`HTTP ${xhr.status}`))
          }
        }
        
        xhr.onerror = () => {
          console.error('XHR 网络错误，可能是 CORS 限制')
          this.loading = false
          this.$message.error('下载失败，请检查 OSS CORS 配置')
          reject(new Error('网络错误'))
        }
        
        xhr.ontimeout = () => {
          console.error('请求超时')
          this.loading = false
          this.$message.error('请求超时')
          reject(new Error('请求超时'))
        }
        
        // 设置超时时间（30 秒）
        xhr.timeout = 30000
        xhr.send()
      })
    },
    
    // 处理 Blob 数据
    processBlob(blob, resolve, reject) {
      this.loadingText = '正在解压文件...'
      
      // 读取为 ArrayBuffer
      const reader = new FileReader()
      reader.onload = () => {
        try {
          const arrayBuffer = reader.result
          console.log('FileReader 读取成功，ArrayBuffer 大小:', arrayBuffer.byteLength)
          
          // 检查是否是 gzip 压缩的文件
          const isGzip = this.checkIfGzip(arrayBuffer)
          console.log('是否为 GZIP 格式:', isGzip)
          
          let pcdData
          if (isGzip) {
            this.loadingText = '正在解压 GZ 文件...'
            console.log('检测到 GZIP 格式，开始解压...')
            // 使用 pako 解压 - 注意要转换为 Uint8Array
            pcdData = pako.inflate(new Uint8Array(arrayBuffer))
            console.log('解压完成，数据大小:', pcdData.length)
          } else {
            console.log('非 GZIP 格式，直接读取')
            pcdData = new Uint8Array(arrayBuffer)
          }
          
          this.loadingText = '正在解析 PCD 文件...'
          console.log('开始解析 PCD 文件...')
          // 解析 PCD 文件 - 确保传入的是 Uint8Array
          const pointsData = this.parsePCD(pcdData)
          
          this.loadingText = '正在创建点云...'
          console.log('开始创建点云，点数:', pointsData.count)
          // 创建点云对象
          this.createPointCloud(pointsData)
          
          console.log('点云渲染完成！')
          this.loading = false
          this.$message.success('点云加载成功！')
          resolve()
        } catch (error) {
          console.error('解析 PCD 文件失败:', error)
          console.error('错误堆栈:', error.stack)
          this.loading = false
          this.$message.error('解析失败：' + error.message)
          reject(error)
        }
      }
      reader.onerror = () => {
        console.error('FileReader 读取失败')
        this.loading = false
        this.$message.error('读取文件失败')
        reject(new Error('读取文件失败'))
      }
      reader.readAsArrayBuffer(blob)
    },
    
    checkIfGzip(data) {
      // Gzip 文件的魔数是 1f 8b
      // 确保 data 是 ArrayBuffer 或 Uint8Array
      let uint8Data
      if (data instanceof ArrayBuffer) {
        uint8Data = new Uint8Array(data)
      } else if (data instanceof Uint8Array) {
        uint8Data = data
      } else {
        console.error('checkIfGzip: 未知的数据类型')
        return false
      }
      
      // 检查前两个字节是否为 0x1f8b
      return uint8Data.length >= 2 && uint8Data[0] === 0x1f && uint8Data[1] === 0x8b
    },
    
    parsePCD(data) {
      console.log('开始解析 PCD，数据大小:', data.length)
      
      const textDecoder = new TextDecoder('utf-8')
      let headerEnd = 0
      let headerText = ''
      
      // 先尝试直接读取前 2KB 查找头部信息
      const sampleSize = Math.min(2048, data.length)
      const dataStr = textDecoder.decode(data.subarray(0, sampleSize))
      console.log('原始数据样本 (前 2KB):', dataStr.substring(0, 1000))
      
      // 按行分割查找 DATA 字段
      const lines = dataStr.split(/\r?\n/)
      let currentPos = 0
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        console.log(`第 ${i} 行:`, line)
        currentPos += line.length + 1 // +1 for newline
        
        if (line.toUpperCase().startsWith('DATA')) {
          headerEnd = currentPos
          headerText = dataStr.substring(0, currentPos)
          break
        }
      }
      
      console.log('找到的头部内容:\n', headerText)
      console.log('头部结束位置:', headerEnd)
      
      if (!headerText.includes('DATA')) {
        throw new Error('无效的 PCD 文件格式：未找到 DATA 字段')
      }
      
      // 解析头部信息
      const headerLines = headerText.split('\n')
      let pointsCount = 0
      let fields = []
      let fieldTypes = []
      let fieldSizes = []
      let fieldCounts = []
      let dataType = 'ascii'
      
      for (const line of headerLines) {
        const parts = line.trim().split(/\s+/)
        const keyword = parts[0].toUpperCase()
        
        if (keyword === 'POINTS') {
          pointsCount = parseInt(parts[1])
        } else if (keyword === 'FIELDS') {
          fields = parts.slice(1)
        } else if (keyword === 'TYPE') {
          fieldTypes = parts.slice(1)
        } else if (keyword === 'SIZE') {
          fieldSizes = parts.slice(1).map(Number)
        } else if (keyword === 'COUNT') {
          fieldCounts = parts.slice(1).map(Number)
        } else if (keyword === 'DATA') {
          dataType = parts[1].toLowerCase()
        }
      }
      
      console.log('数据类型:', dataType)
      console.log('声明的点数:', pointsCount)
      
      // 如果 POINTS 为 0，从实际数据行数计算
      if (pointsCount === 0 && dataType === 'ascii') {
        // 使用完整数据来计算行数
        const fullDataStr = textDecoder.decode(data)
        const dataContent = fullDataStr.substring(headerEnd).trim()
        const dataLines = dataContent.split(/\r?\n/).filter(line => line.trim())
        pointsCount = dataLines.length
        console.log('从数据行数重新计算点数:', pointsCount)
      }
      
      // 计算每个点的字节数
      let pointSize = 0
      const fieldInfo = []
      for (let i = 0; i < fields.length; i++) {
        fieldInfo.push({
          name: fields[i],
          type: fieldTypes[i],
          size: fieldSizes[i],
          count: fieldCounts[i]
        })
        pointSize += fieldSizes[i] * fieldCounts[i]
      }
      
      // 找到 x, y, z 字段的位置
      const xIndex = fields.indexOf('x')
      const yIndex = fields.indexOf('y')
      const zIndex = fields.indexOf('z')
      const rgbIndex = fields.indexOf('rgb')
      
      if (xIndex === -1 || yIndex === -1 || zIndex === -1) {
        throw new Error('PCD 文件缺少 XYZ 坐标字段')
      }
      
      console.log('PCD 信息：点数=', pointsCount, '每个点大小=', pointSize, 'bytes')
      
      // 解析点数据
      const positions = new Float32Array(pointsCount * 3)
      const colors = new Float32Array(pointsCount * 3)
      
      const dataStart = headerEnd
      let offset = 0
      
      console.log('开始解析点数据，点数:', pointsCount, '数据起始位置:', headerEnd, '每个点大小:', pointSize, '数据类型:', dataType)
      
      if (dataType === 'ascii') {
        // ASCII 格式解析 - 读取整个文件数据
        const fullDataStr = textDecoder.decode(data)
        const dataContent = fullDataStr.substring(headerEnd).trim()
        const dataLines = dataContent.split(/\r?\n/).filter(line => line.trim())
        
        console.log('ASCII 数据行数:', dataLines.length, '期望点数:', pointsCount)
        
        // 重新分配数组，使用实际行数
        const actualCount = dataLines.length
        const actualPositions = new Float32Array(actualCount * 3)
        const actualColors = new Float32Array(actualCount * 3)
        
        for (let i = 0; i < actualCount; i++) {
          const parts = dataLines[i].trim().split(/\s+/)
          
          try {
            const x = parseFloat(parts[xIndex])
            const y = parseFloat(parts[yIndex])
            const z = parseFloat(parts[zIndex])
            
            actualPositions[i * 3] = x
            actualPositions[i * 3 + 1] = y
            actualPositions[i * 3 + 2] = z
            
            if (i < 5) {
              console.log(`点 ${i}: x=${x}, y=${y}, z=${z}`)
            }
            
            // 处理颜色字段
            const intensityIndex = fields.indexOf('intensity')
            const rgbIndex = fields.indexOf('rgb')
            
            if (rgbIndex !== -1 && parts[rgbIndex]) {
              // 处理 rgb 字段（PCD中rgb存储为float，但实际是uint32的位打包）
              const rgbFloat = parseFloat(parts[rgbIndex])
              
              // 将 float 转换为 uint32（使用 DataView 进行类型转换）
              const buffer = new ArrayBuffer(4)
              const floatView = new Float32Array(buffer)
              const intView = new Uint32Array(buffer)
              floatView[0] = rgbFloat
              const rgbInt = intView[0]
              
              // 从 uint32 中提取 R, G, B 分量
              const r = ((rgbInt >> 16) & 0xFF) / 255
              const g = ((rgbInt >> 8) & 0xFF) / 255
              const b = (rgbInt & 0xFF) / 255
              
              actualColors[i * 3] = r
              actualColors[i * 3 + 1] = g
              actualColors[i * 3 + 2] = b
              
              if (i < 5) {
                console.log(`点 ${i} RGB: float=${rgbFloat}, int=${rgbInt}, r=${r.toFixed(2)}, g=${g.toFixed(2)}, b=${b.toFixed(2)}`)
              }
            } else if (intensityIndex !== -1 && parts[intensityIndex]) {
              // 处理 intensity 字段
              const intensity = parseFloat(parts[intensityIndex])
              const normalized = Math.min(1, intensity / 255)
              actualColors[i * 3] = normalized
              actualColors[i * 3 + 1] = normalized
              actualColors[i * 3 + 2] = normalized
            } else {
              // 默认白色
              actualColors[i * 3] = 1
              actualColors[i * 3 + 1] = 1
              actualColors[i * 3 + 2] = 1
            }
          } catch (e) {
            console.error(`解析第 ${i} 行失败:`, e, dataLines[i])
          }
        }
        
        return {
          positions: actualPositions,
          colors: actualColors,
          count: actualCount
        }
      } else {
        // 二进制格式解析（原有代码）
        const dataStart = headerEnd
        let offset = 0
        
        for (let i = 0; i < pointsCount && i < 10; i++) {
          const pointOffset = dataStart + i * pointSize
          
          if (fieldTypes[xIndex] === 'F') {
            try {
              const x = new DataView(data.buffer, data.byteOffset + pointOffset, 4).getFloat32(0, true)
              const y = new DataView(data.buffer, data.byteOffset + pointOffset + fieldSizes[xIndex], 4).getFloat32(0, true)
              const z = new DataView(data.buffer, data.byteOffset + pointOffset + fieldSizes[xIndex] + fieldSizes[yIndex], 4).getFloat32(0, true)
              
              positions[i * 3] = x
              positions[i * 3 + 1] = y
              positions[i * 3 + 2] = z
              
              if (i < 10) {
                console.log(`点 ${i}: x=${x}, y=${y}, z=${z}`)
              }
            } catch (e) {
              console.error(`解析点 ${i} 失败:`, e)
              throw new Error(`解析点 ${i} 时出错：${e.message}`)
            }
          }
        }
        
        // 继续解析剩余的点
        for (let i = 10; i < pointsCount; i++) {
          const pointOffset = dataStart + i * pointSize
          
          if (fieldTypes[xIndex] === 'F') {
            const x = new DataView(data.buffer, data.byteOffset + pointOffset, 4).getFloat32(0, true)
            const y = new DataView(data.buffer, data.byteOffset + pointOffset + fieldSizes[xIndex], 4).getFloat32(0, true)
            const z = new DataView(data.buffer, data.byteOffset + pointOffset + fieldSizes[xIndex] + fieldSizes[yIndex], 4).getFloat32(0, true)
            
            positions[i * 3] = x
            positions[i * 3 + 1] = y
            positions[i * 3 + 2] = z
          }
        }
      }
      
      return {
        positions,
        colors,
        count: pointsCount
      }
    },
    
    createPointCloud(points) {
      console.log('创建点云，点数:', points.count)
      
      if (!this.scene) {
        console.error('scene 不存在，无法创建点云')
        return null
      }
      
      this.clearPointCloud()
      
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute('position', new THREE.BufferAttribute(points.positions, 3))
      geometry.setAttribute('color', new THREE.BufferAttribute(points.colors, 3))
      
      // 根据点数动态调整点的大小
      let pointSize = 0.3
      if (points.count > 100000) {
        pointSize = 0.05
      } else if (points.count > 10000) {
        pointSize = 0.1
      } else if (points.count > 1000) {
        pointSize = 0.2
      } else {
        pointSize = 0.3
      }
      
      console.log('点大小设置为:', pointSize)
      
      const material = new THREE.PointsMaterial({
        size: pointSize,
        vertexColors: true,
        sizeAttenuation: true
      })
      
      this.pointCloud = new THREE.Points(geometry, material)
      this.scene.add(this.pointCloud)
      
      // 自动调整相机位置以包含所有点
      this.autoFitCamera(points.positions)
      
      // 返回点云数据用于缓存
      return points
    },
    
    autoFitCamera(positions) {
      if (!this.scene || !this.controls) {
        console.error('scene 或 controls 不存在')
        return
      }
      
      // 计算点云的包围盒
      const box = new THREE.Box3()
      const positionAttribute = new THREE.BufferAttribute(positions, 3)
      
      for (let i = 0; i < positionAttribute.count; i++) {
        const x = positionAttribute.getX(i)
        const y = positionAttribute.getY(i)
        const z = positionAttribute.getZ(i)
        box.expandByPoint(new THREE.Vector3(x, y, z))
      }
      
      // 计算中心点和大小
      const center = box.getCenter(new THREE.Vector3())
      const size = box.getSize(new THREE.Vector3())
      const maxDim = Math.max(size.x, size.y, size.z)
      
      // 调整相机位置
      const distance = maxDim * 2
      this.controls.spherical.radius = distance
      this.updateCameraPosition(this.controls.spherical)
    },
    
    clearPointCloud() {
      console.log('清理点云，当前 scene:', !!this.scene)
      if (this.scene && this.pointCloud) {
        try {
          // 只移除点云
          this.scene.remove(this.pointCloud)
          if (this.pointCloud.geometry) {
            this.pointCloud.geometry.dispose()
          }
          if (this.pointCloud.material) {
            this.pointCloud.material.dispose()
          }
          this.pointCloud = null
          
          console.log('点云清理完成，剩余子对象数:', this.scene.children.length)
        } catch (error) {
          console.error('清理点云失败:', error)
        }
      }
    },
    
    dispose() {
      console.log('销毁点云查看器')
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
      }
      
      // 清理渲染器
      if (this.renderer) {
        try {
          this.renderer.dispose()
          if (this.renderer.domElement && this.renderer.domElement.parentNode) {
            this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
          }
        } catch (error) {
          console.error('销毁渲染器失败:', error)
        }
        this.renderer = null
      }
      
      // 清理场景（包括所有子对象）
      if (this.scene) {
        while(this.scene.children.length > 0) { 
          const obj = this.scene.children[0]
          if (obj.geometry) obj.geometry.dispose()
          if (obj.material) obj.material.dispose()
          this.scene.remove(obj)
        }
        this.scene = null
      }
      
      // 清理其他引用
      this.pointCloud = null
      this.camera = null
      this.controls = null
      window.removeEventListener('resize', this.onWindowResize)
      
      console.log('销毁完成')
    },
    
    resetView() {
      if (this.pointCloud) {
        const positions = this.pointCloud.geometry.attributes.position.array
        this.autoFitCamera(positions)
      }
    },
    
    handleDownloadPCD() {
      if (!this.currentFileUrl) return
      const link = document.createElement('a')
      link.href = this.currentFileUrl
      link.download = this.currentFileName || 'pointcloud.pcd.gz'
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$message.success('开始下载...')
    }
  }
}
</script>

<style lang="less" scoped>
.pcd-viewer-container {
  position: relative;
  height: 70vh;
  
  .canvas-container {
    width: 100%;
    height: 100%;
    background: #1a1a2e;
  }
  
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 10;
  }
  
  .controls {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    gap: 8px;
    z-index: 5;
  }
}
</style>
